import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccArrowCont,
  AccordionComponentCont,
  AccordionTableTileCont,
  AccTitle,
  RepAccTileBodyCont,
  TileAccRightSide,
  TileAccSide,
  ScoreVal,
  TileAccBodyContent,
  ExampleStatementTitle,
  ExampleStatementCont,
  ExampleStatement,
} from './CategoriesAndTopicsTileAccordionEl.styles';
import { getLocaleFormatDate } from '../../../../helpers/helpers';
import { useFilters } from '../../../../hooks/useFilters';
import { ITopicResults } from '../../../../services/reports';
import AgreementIndicator from '../../../AgreementIndicator/AgreementIndicator';
import LittleWormChart from '../../../LittleWormChart/LittleWormChart';
import ProgressBar from '../../../ProgressBar/ProgressBar';
import ScoreTrend from '../../../ScoreTrend/ScoreTrend';
import Arrow from '@/assets/icons/chevron-left.svg?react';

interface ICategoriesAndTopicsTileAccordionEl {
  parentCode: string;
  item: ITopicResults;
  reportKey: string;
  isTotalsTabActive: boolean;
}

const CategoriesAndTopicsTileAccordionEl: FC<ICategoriesAndTopicsTileAccordionEl> = ({
  item,
  parentCode,
  reportKey,
  isTotalsTabActive,
}) => {
  const { t, i18n } = useTranslation();
  const { score, scoreRating, topic, agreementLevel, teamDistribution, benchmarks } = item;
  const { code, title, exampleStatement, description } = topic;
  const { getNestedFilters, applyNestedFilter } = useFilters();
  const reportTileFilters = getNestedFilters(reportKey);
  const thisTileFilters = reportTileFilters[parentCode][code] || {};
  const active = thisTileFilters.active || false;

  const { historical, survey } = benchmarks ?? {};

  const manageNestedFilters = () => {
    if (active) {
      return applyNestedFilter({ [code]: null }, parentCode, reportKey);
    }

    return applyNestedFilter({ [code]: { active: !active } }, parentCode, reportKey);
  };

  return (
    <AccordionComponentCont id={item?.topic?.code} onClick={manageNestedFilters}>
      <AccordionTableTileCont id="AccordionTableTileCont">
        <TileAccSide data-testid="activeNestedReportFilter" id="TileAccSide">
          <AccArrowCont id="AccArrowCont" active={active}>
            <Arrow />
          </AccArrowCont>
          <AccTitle id="AccTitle">{title}</AccTitle>
          {isTotalsTabActive && (
            <ScoreVal id="ScoreVal">
              {score}
              {historical ? (
                <ScoreTrend
                  progress={historical?.change}
                  stateType={historical?.changeDirectionality}
                  isBorderless
                />
              ) : null}
            </ScoreVal>
          )}
        </TileAccSide>
        <TileAccRightSide id="TileAccRightSide">
          {isTotalsTabActive ? (
            <>
              <ProgressBar
                barMode={scoreRating}
                contWidth="100%"
                height="16px"
                historicalScore={historical?.score}
                progress={`${score}%`}
                tooltip={`${getLocaleFormatDate(survey?.endDate ?? '', i18n.language)} ${
                  survey?.type
                }`}
              />
              <AgreementIndicator mode={agreementLevel} />
            </>
          ) : (
            teamDistribution && (
              <LittleWormChart
                weakValue={teamDistribution.weak}
                normalValue={teamDistribution.normal}
                strongValue={teamDistribution.strong}
                size="tiny"
              />
            )
          )}
        </TileAccRightSide>
      </AccordionTableTileCont>
      {active && (
        <RepAccTileBodyCont id="RepAccTileBodyCont">
          <TileAccBodyContent id="TileAccBodyContent">
            <div dangerouslySetInnerHTML={{ __html: description || '' }} />
            {exampleStatement && (
              <ExampleStatementCont id="ExampleStatementCont">
                <ExampleStatementTitle id="ExampleStatementTitle">
                  {t('report.results.exampleStatement')}:
                </ExampleStatementTitle>{' '}
                <ExampleStatement>{exampleStatement}</ExampleStatement>
              </ExampleStatementCont>
            )}
          </TileAccBodyContent>
        </RepAccTileBodyCont>
      )}
    </AccordionComponentCont>
  );
};
export default CategoriesAndTopicsTileAccordionEl;
